import { getStackZIndex, useModalStackDepth } from '@/contexts/ModalStackContext';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';

/**
 * VideoPlayerModal Component
 *
 * A modal dialog for playing videos with full controls
 * - Dark background for cinema feel
 * - Native HTML5 video controls
 * - Click outside to close
 * - Escape key to close
 * - Responsive sizing
 */

interface VideoPlayerModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** URL of the video to play (S3/CDN). Unused when iframeUrl is provided. */
  videoUrl: string;
  /** Optional Google Drive preview URL. When set, renders an iframe instead
   *  of a native <video> element — used for deferred GDrive media pre-publish. */
  iframeUrl?: string;
  /** Optional video title/filename */
  title?: string;
}

export const VideoPlayerModal = ({
  isOpen,
  onClose,
  videoUrl,
  iframeUrl,
  title,
}: VideoPlayerModalProps) => {
  const { t } = useTranslation();
  const stackZ = getStackZIndex(useModalStackDepth(isOpen));
  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        {/* Backdrop with flexbox centering */}
        <Dialog.Overlay
          style={{ zIndex: stackZ.backdrop }}
          className={cn(
            'fixed inset-0 bg-black/80 backdrop-blur-sm',
            'flex items-center justify-center',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
        >
          {/* Modal Content */}
          <Dialog.Content
            className={cn(
              // Size
              'w-[95vw] max-w-4xl',

              // Appearance - Dark background for cinema feel
              'bg-gray-900 rounded-2xl shadow-xl',

              // Z-index layer - above nested overlay
              'z-[101]',

              // Layout
              'relative',

              // Max height: gap scales with viewport (small screens=tight, large=breathable)
              'max-h-[calc(100vh-clamp(2rem,5vh,8rem))]',

              // Focus and animations
              'focus:outline-none',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'duration-150'
            )}
          >
            {/* Accessible title - always required by Radix, visually hidden when no title */}
            {title ? (
              <div className="px-6 pt-6 pb-3">
                <Dialog.Title className="text-lg font-semibold text-white truncate">
                  {title}
                </Dialog.Title>
              </div>
            ) : (
              // eslint-disable-next-line i18next/no-literal-string
              <Dialog.Title className="sr-only">Video Player</Dialog.Title>
            )}

            {/* eslint-disable-next-line i18next/no-literal-string */}
            {/* Accessible description */}
            <Dialog.Description className="sr-only">
              {title ? `Playing video: ${title}` : 'Video player dialog'}
            </Dialog.Description>

            {/* Close button - Positioned absolute top-right */}
            <Dialog.Close
              className={cn(
                'absolute top-4 right-4 z-10',
                'w-10 h-10 rounded-full',
                'flex items-center justify-center',
                'bg-black/50 hover:bg-black/70',
                'text-white hover:text-gray-200',
                'transition-all duration-200',
                'focus:outline-none'
              )}
              aria-label="Close video player"
            >
              <Icon name={ICONS.CLOSE} size="sm" />
            </Dialog.Close>

            {/* Video / iframe container */}
            <div className="px-6 pb-6">
              {iframeUrl ? (
                <div
                  className="w-full rounded-lg overflow-hidden bg-black"
                  style={{ aspectRatio: '16/9', maxHeight: 'calc(100vh - 6rem)' }}
                >
                  <iframe
                    src={iframeUrl}
                    allow="autoplay; encrypted-media; fullscreen"
                    allowFullScreen
                    className="w-full h-full"
                    title={title ?? 'Google Drive preview'}
                  />
                </div>
              ) : videoUrl ? (
                <video
                  src={videoUrl}
                  controls
                  className="w-full h-auto rounded-lg"
                  style={{ maxHeight: 'calc(100vh - 6rem)' }}
                >
                  {t('video_browser_unsupported')}
                </video>
              ) : (
                <div className="w-full flex items-center justify-center rounded-lg bg-gray-800 text-gray-400 text-sm py-16">
                  {t('video_preview_unavailable', { defaultValue: 'Preview not available.' })}
                </div>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
